/**
 * ComplianceTooltip Component
 * AS9100D Audit-Ready Three-Tier Disclosure Pattern
 * 
 * Tier 1: Blue/gray circle indicator on field label
 * Tier 2: Quick tooltip on hover (500ms delay)
 * Tier 3: Full compliance modal on click
 * 
 * @version 1.0.0
 * @date January 2026
 */
import React, { useState, useCallback } from 'react';
import { 
  Info, 
  FileText, 
  AlertCircle, 
  ChevronRight,
  ExternalLink,
  CheckCircle2,
  AlertTriangle,
  Eye
} from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from './ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { getTooltip, isCriticalClause, AS9100D_CLAUSES } from '../data/complianceTooltips';

/**
 * Compliance Indicator - The small circle next to field labels
 */
const ComplianceIndicator = ({ isCritical, isActive }) => {
  return (
    <div 
      className={`
        w-3.5 h-3.5 rounded-full flex items-center justify-center cursor-pointer
        transition-all duration-200 hover:scale-110
        ${isCritical 
          ? 'bg-blue-100 text-blue-600 hover:bg-blue-200' 
          : 'bg-gray-100 text-gray-500 hover:bg-gray-200'
        }
      `}
    >
      <Info className="w-2.5 h-2.5" />
    </div>
  );
};

/**
 * Quick Info Card - Tier 2 Tooltip Content
 */
const QuickInfoCard = ({ quickInfo, fieldName, onOpenModal }) => {
  const { primaryClause, whyExists, complianceStatus } = quickInfo;
  
  const statusColors = {
    active: 'bg-green-100 text-green-700',
    monitoring: 'bg-amber-100 text-amber-700',
    warning: 'bg-red-100 text-red-700'
  };
  
  const statusLabels = {
    active: 'Active Control',
    monitoring: 'Monitoring',
    warning: 'Attention Needed'
  };

  return (
    <div className="w-72 p-0">
      {/* Header */}
      <div className="bg-[#101957] text-white p-3 rounded-t-lg">
        <div className="flex items-center justify-between">
          <span className="text-xs font-semibold uppercase tracking-wide opacity-80">
            AS9100D Compliance
          </span>
          <Badge 
            variant="secondary" 
            className={`text-xs ${statusColors[complianceStatus]}`}
          >
            {statusLabels[complianceStatus]}
          </Badge>
        </div>
        <h4 className="text-sm font-medium mt-1">{fieldName}</h4>
      </div>
      
      {/* Content */}
      <div className="p-3 space-y-3">
        {/* Primary Clause */}
        <div className="flex items-start gap-2">
          <FileText className="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" />
          <div>
            <p className="text-xs font-medium text-[#101957]">
              Clause {primaryClause.number}: {primaryClause.title}
            </p>
            <p className="text-xs text-gray-500 mt-0.5">{primaryClause.summary}</p>
          </div>
        </div>
        
        {/* Why This Exists */}
        <div className="flex items-start gap-2">
          <AlertCircle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-gray-600">{whyExists}</p>
        </div>
        
        {/* Learn More */}
        <button
          onClick={onOpenModal}
          className="w-full flex items-center justify-between p-2 bg-gray-50 hover:bg-gray-100 rounded text-xs text-[#101957] font-medium transition-colors"
        >
          <span>View Full Compliance Context</span>
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

/**
 * Full Context Modal - Tier 3 Deep Dive
 */
const FullContextModal = ({ isOpen, onClose, tooltip }) => {
  if (!tooltip) return null;
  
  const { fieldName, fullContext, criticality } = tooltip;
  const { 
    primaryClause, 
    supportingClauses, 
    controlDescription, 
    evidenceCreated,
    auditorQA,
    relatedActions 
  } = fullContext;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh]">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <div className={`
              w-8 h-8 rounded-full flex items-center justify-center
              ${criticality === 'critical' ? 'bg-blue-100 text-blue-600' : 'bg-gray-100 text-gray-600'}
            `}>
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <DialogTitle className="text-lg text-[#101957]">
                {fieldName}
              </DialogTitle>
              <DialogDescription className="text-sm">
                AS9100D Compliance Context
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        <ScrollArea className="max-h-[calc(90vh-180px)] pr-4">
          <div className="space-y-6">
            {/* Primary Clause */}
            <section>
              <h3 className="text-sm font-semibold text-[#101957] mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-blue-600" />
                Primary Clause: {primaryClause.number}
              </h3>
              <div className="bg-blue-50 border-l-4 border-blue-500 p-4 rounded-r">
                <p className="font-medium text-blue-900 mb-2">{primaryClause.title}</p>
                <p className="text-sm text-blue-800 italic">
                  "{primaryClause.quotedText || primaryClause.fullText}"
                </p>
                {primaryClause.aerospaceNote && (
                  <p className="text-sm text-blue-700 mt-2 pt-2 border-t border-blue-200">
                    <strong>Aerospace Note:</strong> {primaryClause.aerospaceNote}
                  </p>
                )}
              </div>
            </section>
            
            {/* Supporting Clauses */}
            {supportingClauses && supportingClauses.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-[#101957] mb-2 flex items-center gap-2">
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                  Supporting Clauses
                </h3>
                <div className="space-y-2">
                  {supportingClauses.map((clause, idx) => (
                    <div key={idx} className="flex items-start gap-2 p-2 bg-gray-50 rounded">
                      <Badge variant="outline" className="text-xs shrink-0">
                        {clause.number}
                      </Badge>
                      <div>
                        <p className="text-sm font-medium">{clause.title}</p>
                        <p className="text-xs text-gray-600">{clause.summary}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            )}
            
            <Separator />
            
            {/* Control Description */}
            <section>
              <h3 className="text-sm font-semibold text-[#101957] mb-3 flex items-center gap-2">
                <Eye className="w-4 h-4 text-green-600" />
                How This Control Works
              </h3>
              
              {/* How It Works */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">Implementation:</p>
                <ul className="space-y-1.5">
                  {controlDescription.howItWorks.map((item, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                      <CheckCircle2 className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Validation Rules */}
              <div className="mb-4">
                <p className="text-xs font-medium text-gray-700 mb-2">System Validation:</p>
                <ul className="space-y-1.5">
                  {controlDescription.validationRules.map((rule, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-gray-600">
                      <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                      <span>{rule}</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* Linkages */}
              {controlDescription.linkages && (
                <div>
                  <p className="text-xs font-medium text-gray-700 mb-2">System Linkages:</p>
                  <div className="flex flex-wrap gap-1.5">
                    {controlDescription.linkages.map((link, idx) => (
                      <Badge key={idx} variant="secondary" className="text-xs">
                        {link}
                      </Badge>
                    ))}
                  </div>
                </div>
              )}
            </section>
            
            <Separator />
            
            {/* Evidence Created */}
            <section>
              <h3 className="text-sm font-semibold text-[#101957] mb-2 flex items-center gap-2">
                <FileText className="w-4 h-4 text-purple-600" />
                Audit Evidence Created
              </h3>
              <div className="bg-purple-50 p-3 rounded">
                <ul className="space-y-1.5">
                  {evidenceCreated.map((evidence, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-purple-800">
                      <span className="text-purple-500">•</span>
                      <span>{evidence}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </section>
            
            {/* Auditor Q&A */}
            {auditorQA && auditorQA.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-[#101957] mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4 text-amber-600" />
                  Typical Auditor Questions
                </h3>
                <div className="space-y-3">
                  {auditorQA.map((qa, idx) => (
                    <div key={idx} className="bg-amber-50 border border-amber-200 p-3 rounded">
                      <p className="text-sm font-medium text-amber-900 mb-2">
                        Q: {qa.question}
                      </p>
                      <p className="text-sm text-amber-800 pl-3 border-l-2 border-amber-300">
                        <strong>Suggested Answer:</strong> {qa.suggestedAnswer}
                      </p>
                    </div>
                  ))}
                </div>
              </section>
            )}
            
            {/* Related Actions */}
            {relatedActions && relatedActions.length > 0 && (
              <section>
                <h3 className="text-sm font-semibold text-[#101957] mb-2">
                  Quick Actions
                </h3>
                <div className="flex flex-wrap gap-2">
                  {relatedActions.map((action, idx) => (
                    <Button 
                      key={idx}
                      variant="outline" 
                      size="sm"
                      className="text-xs"
                    >
                      {action.label}
                      <ExternalLink className="w-3 h-3 ml-1" />
                    </Button>
                  ))}
                </div>
              </section>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Main ComplianceTooltip Component
 * 
 * Usage:
 * <ComplianceTooltip tooltipId="stock.lot_number">
 *   <Label>Lot Number</Label>
 * </ComplianceTooltip>
 */
const ComplianceTooltip = ({ 
  tooltipId, 
  children,
  showIndicator = true,
  position = 'inline' // 'inline' or 'after'
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const tooltip = getTooltip(tooltipId);
  
  const handleOpenModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);
  
  const handleCloseModal = useCallback(() => {
    setIsModalOpen(false);
  }, []);
  
  if (!tooltip) {
    // If no tooltip data, just render children
    return children;
  }
  
  const isCritical = tooltip.criticality === 'critical';
  
  return (
    <div className={`flex items-center ${position === 'inline' ? 'gap-1.5' : 'gap-2'}`}>
      {children}
      
      {showIndicator && (
        <TooltipProvider delayDuration={500}>
          <Tooltip>
            <TooltipTrigger asChild>
              <button
                type="button"
                onClick={handleOpenModal}
                className="focus:outline-none focus:ring-2 focus:ring-blue-300 rounded-full"
                data-testid={`compliance-tooltip-${tooltipId}`}
              >
                <ComplianceIndicator isCritical={isCritical} />
              </button>
            </TooltipTrigger>
            <TooltipContent 
              side="right" 
              align="start"
              className="p-0 bg-white border-0 shadow-xl rounded-lg"
            >
              <QuickInfoCard 
                quickInfo={tooltip.quickInfo} 
                fieldName={tooltip.fieldName}
                onOpenModal={handleOpenModal}
              />
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      )}
      
      <FullContextModal 
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        tooltip={tooltip}
      />
    </div>
  );
};

/**
 * Compliance Label - Label with built-in compliance tooltip
 * 
 * Usage:
 * <ComplianceLabel tooltipId="stock.lot_number">Lot Number</ComplianceLabel>
 */
export const ComplianceLabel = ({ 
  tooltipId, 
  children, 
  className = '',
  required = false 
}) => {
  return (
    <ComplianceTooltip tooltipId={tooltipId}>
      <label className={`text-sm font-medium text-[#101957] ${className}`}>
        {children}
        {required && <span className="text-red-500 ml-1">*</span>}
      </label>
    </ComplianceTooltip>
  );
};

/**
 * Compliance Field Group - Complete field with label and compliance
 * 
 * Usage:
 * <ComplianceFieldGroup 
 *   tooltipId="stock.lot_number"
 *   label="Lot Number"
 *   required
 * >
 *   <Input value={lotNumber} />
 * </ComplianceFieldGroup>
 */
export const ComplianceFieldGroup = ({
  tooltipId,
  label,
  children,
  required = false,
  description,
  className = ''
}) => {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <ComplianceLabel tooltipId={tooltipId} required={required}>
        {label}
      </ComplianceLabel>
      {children}
      {description && (
        <p className="text-xs text-gray-500">{description}</p>
      )}
    </div>
  );
};

export default ComplianceTooltip;
